import * as React from 'react'
import { StoreProps } from '../../../../services/stores/casting.store'
import { I18nService } from '../../../../services/utils/i18n.service'
import { modalOpenDecorated } from '../../../../services/utils/modals.service'
import { RansackQuery as EmployeeRansackQuery, getUnavailabilityMessage } from '../../../../services/models/employees.service'
import { RansackQuery as ResourceRansackQuery } from '../../../../services/models/offered-service-resources.service'
import { TypedID } from '../../../../services/utils/iots.service'
import { PrimaryButton } from '../casting-styled.component'
import OfferedServiceResourcesPickerModalComponent from '../../../backoffice/offered-service-resources/offered-service-resources-picker-modal.component'
import TooltipComponent from '../../../shared/tooltip.component'

const i18nScoped = new I18nService(require('./slot-assignable-picker-button.component.yml'))

type Props = StoreProps & { slot: StoreProps['data']['slots'][0], titleStyle?: React.CSSProperties }

export default function SlotAssignablePickerButton ({ store, data: { market, slots, offeredService: os, city, requiredPlace, serviceDeliveryLocation: sdl, urssafPayment, selectedItemPreferenceIds }, slot, titleStyle = { marginBottom: 10 } }: Props) {
  const available = store.asyncComputedProperties.isAssignableAvailableForSlot.readOrAsyncCompute([slot, os])
  const disabled = (market['solo_edition_active?'] || window.currentUser?.type === 'Employee') && !!slot.assignable

  const pickEmployee = React.useCallback(async () => {
    const EmployeesPickerModalComponent = await import('../../../backoffice/employees/picker/employees-picker-modal.component')
    const query: EmployeeRansackQuery = {
      'q[employee_information_state_in]': ['published'],
      'q[employee_information_offered_services_id_in]': os.id,
      'q[item_preferences_id_in]': selectedItemPreferenceIds[0],
      'q[intervention_sector]': ['online', 'phone'].includes(requiredPlace) ? undefined : city.content,
      "q[with_intervention_places]": requiredPlace,
      "q[service_delivery_location_id_eq]": sdl?.id,
      'available_for': store.buildAvailableBodyForSlot(slot),
      with_urssaf_payment: urssafPayment
    }

    const employee = await modalOpenDecorated<{ id: TypedID<'Employee'>, name: string }>(
      i18nScoped.t('pick_employee'),
      controls => <EmployeesPickerModalComponent.default
        { ...controls }
        offeredServices={ [{ id: os.id, name: os.name, offered_service_type: os.offered_service_type }] }
        preferences={ os.preferences }
        enableUrssafPaymentFilter={ market['urssaf_payment?'] }
        allowedInterventionPlaces={ [requiredPlace] }
        serviceDeliveryLocations={ sdl?.id ? [{ id: sdl.id, ...sdl }] : [] }
        query={ query }
        availableForBody={ query.available_for } />
    )

    if (!employee) return

    const newSlot = { ...slot, assignable: { id: employee.id, name: employee.name, available: null as boolean } }
    store.setSlots(slots.map(_ => _ === slot ? newSlot : _))
  }, [city.content, market, os.id, os.name, os.offered_service_type, os.preferences, requiredPlace, sdl, selectedItemPreferenceIds, slot, slots, store, urssafPayment])

  const pickOfferedServiceResource = React.useCallback(async () => {
    const query: ResourceRansackQuery = {
      'q[find_by_offered_service]': os.id,
      'q[intervention_sector]': ['online', 'phone'].includes(requiredPlace) ? undefined : city.content,
      'q[find_by_service_delivery_location]': sdl?.id,
      'q[deactivated_eq]': false,
      'available_for': store.buildAvailableBodyForSlot(slot)
    }

    const offeredServiceResource = await modalOpenDecorated<{ id: TypedID<'OfferedServiceResource'>, ref: string }>(
      i18nScoped.t('pick_offered_service_resource'),
      controls => <OfferedServiceResourcesPickerModalComponent
        { ...controls }
        offeredServices={ [{ id: os.id, name: os.name, offered_service_type: os.offered_service_type }] }
        serviceDeliveryLocations={ sdl?.id ? [{ id: sdl.id, ...sdl }] : [] }
        query={ query }
        availableForBody={ query.available_for } />,
      { outerModalClass: 'modal--XL' }
    )

    if (!offeredServiceResource) return

    const newSlot = { ...slot, assignable: { id: offeredServiceResource.id, name: offeredServiceResource.ref } }
    store.setSlots(slots.map(_ => _ === slot ? newSlot : _))
  }, [city.content, os.id, os.name, os.offered_service_type, requiredPlace, sdl, slot, slots, store])

  const pickAssignable = React.useCallback(() => {
    return os.offered_service_type === 'offered_service' ? pickEmployee() : pickOfferedServiceResource()
  }, [os.offered_service_type, pickEmployee, pickOfferedServiceResource])

  return <React.Fragment>
    <div style={ titleStyle }>
      { i18nScoped.t(os.offered_service_type === 'offered_service' ? 'employee' : 'offered_service_resource') }&nbsp;
      { available?.available === true && <span className="u-txtClrSuccess">{ i18nScoped.t('available') }</span> }
      { available?.available === false && <TooltipComponent
        message={ getUnavailabilityMessage(available) }
        style={{ display: 'inline-flex' }}
        innerStyle={{ width: '20em' }}
        children={ <span className="u-txtClrError">{ i18nScoped.t('unavailable') }</span> } /> }
    </div>

    { !slot.assignable && <PrimaryButton style={{ padding: '0.5em 1em', display: 'block' }} onClick={ pickAssignable }>
      { i18nScoped.t(`pick_${os.offered_service_type === 'offered_service' ? 'employee' : 'offered_service_resource'}`) }
    </PrimaryButton> }
    
    { slot.assignable && <PrimaryButton style={{ padding: '0.5em 1em', display: 'block' }} onClick={ () => !disabled && pickAssignable() } $disabled={ disabled }>
      { slot.assignable.name }
    </PrimaryButton> }
  </React.Fragment>
}
